import React, { useEffect, useRef } from "react";
import useEscapeLayer from "../useEscape";
import { changeStatusOptions, HOLD_PRESETS } from "./endings.mjs";
import { confirmIdFor } from "./statusConfirm.mjs";

/*
 * CHANGE STATUS…: the menu under the position pill (frame 1i, drawn at Agreement +
 * legal; the brief, item 11).
 *
 * Only what fits now, from record/endings.mjs changeStatusOptions, so this file
 * decides nothing about which options a company gets:
 *   Move to       the other open stages, each with what the move means from where
 *                 the company is ("Pricing sent · agreement pulled back"), then
 *                 Closed won… ("pick the won date · no signed copy yet")
 *   Hold until…   the server's presets as chips under it, then A date…
 *   Not now       only when something in Next is asking
 *   the endings   Remove from pipeline…, Closed lost… (greyed with why until it is
 *                 earned: a call, and pricing for a no), Disqualify…, Mark as
 *                 vendor, Not a lead… (never on a client or former client), and on
 *                 a client Mark churned…
 *
 * THE MENU NEVER WRITES. Every option opens its confirm (ConfirmCard, through
 * ops.openEnding), stage moves and Not now included, because each one changes the
 * company on the board and in the reports, and the confirm is where it says what it
 * changes and takes a note for the timeline. A hold chip opens the hold confirm
 * with that day already picked, so a hold is still two clicks, not a form.
 *
 * Esc closes the menu and nothing behind it (useEscape.js), and the arrow keys
 * move through the options. Where it sits is the header's business: a popover
 * under the pill, closed by a click anywhere else, or on the phone a bottom sheet
 * (RecordHeader wraps it in BottomSheet, which owns Esc there), so this renders
 * the rows only.
 *
 * Props:
 *   record   the shell's bundle: company, contact, act, layout
 *   ops      ops.closeStatus, ops.openEnding(id, preset)
 *   sections the options, when the host has worked them out itself: the Leads
 *            page's row and bulk menu (leadsStatus.mjs), which is this menu for
 *            one row and for a selection
 */
export default function ChangeStatusMenu({ record, ops, sections: given = null }) {
  const { company, contact, act } = record;
  const sheet = record.layout === "phone";
  const ref = useRef(null);
  const sections = given || changeStatusOptions({ company, contact, act });

  // On the phone the sheet around the menu registers the Esc layer; a second one
  // here would make Esc take two presses to close one thing.
  useEscapeLayer(!sheet, () => ops.closeStatus());

  // Focus moves into the menu so the arrow keys work at once, and goes back to
  // what opened it (the pill) when the menu closes while holding the focus.
  useEffect(() => {
    const before = typeof document !== "undefined" ? document.activeElement : null;
    const el = ref.current;
    if (el && typeof el.focus === "function") el.focus({ preventScroll: true });
    return () => {
      if (!before || typeof before.focus !== "function" || !before.isConnected) return;
      const active = document.activeElement;
      if (!active || active === document.body || (el && el.contains(active))) before.focus({ preventScroll: true });
    };
  }, []);

  const open = (id, preset) => {
    if (id) ops.openEnding(id, preset || {});
  };

  // Up / Down / Home / End through the options; Tab still walks every control.
  const onKeyDown = (e) => {
    const keys = { ArrowDown: 1, ArrowUp: -1, Home: "first", End: "last" };
    if (!(e.key in keys) || !ref.current) return;
    const items = [...ref.current.querySelectorAll('[role="menuitem"]')];
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement);
    const step = keys[e.key];
    const to = step === "first" ? 0 : step === "last" ? items.length - 1 : at < 0 ? (step > 0 ? 0 : items.length - 1) : (at + step + items.length) % items.length;
    items[to].focus();
  };

  return (
    <div
      ref={ref}
      className="rec-menu rec-csm"
      role="menu"
      aria-label="Change status"
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      {!sections.length && <div className="rec-menu__label">Nothing to change from here.</div>}
      {sections.map((section, si) => (
        <React.Fragment key={section.id}>
          {si > 0 && <div className="rec-menu__sep" role="separator" />}
          {section.label && <div className="rec-menu__label">{section.label}</div>}
          {section.items.map((item) => (
            <React.Fragment key={item.id}>
              <button
                type="button"
                role="menuitem"
                className={`rec-menu__item rec-csm__item${item.disabled ? " is-off" : ""}`}
                // Focusable while unavailable, so the reason under it can be read
                // and the arrow keys do not skip a row the eye can see.
                aria-disabled={item.disabled || undefined}
                onClick={() => !item.disabled && open(confirmIdFor(item))}
              >
                <span className="rec-csm__label">{item.label}</span>
                <span className="rec-menu__hint">{item.hint}</span>
              </button>
              {item.disabled && item.why && <div className="rec-csm__why">{item.why}</div>}
              {item.id === "hold" && (
                <div className="rec-menu__presets rec-csm__presets">
                  {HOLD_PRESETS.map((p) => (
                    <button key={p.id} type="button" className="rec-chip" onClick={() => open("hold", { preset: p.id })}>
                      {p.label}
                    </button>
                  ))}
                  <button type="button" className="rec-chip" onClick={() => open("hold", { preset: "date" })}>
                    A date…
                  </button>
                </div>
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}
